import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { formatPreFinanceMoney, formatPreFinancePeriod } from "../utils/preFinancePreviewUtils";

const StackField = ({ label, value }) => (
  <>
    <Grid item xs={5}>
      <Typography>{label}:</Typography>
    </Grid>
    <Grid item xs={5}>{value ?? "—"}</Grid>
  </>
);

const SummaryPanelRow = ({ label, value }) => (
  <Grid container sx={{ mb: 0.25 }}>
    <Grid item xs={8}>
      <Typography sx={{ fontSize: 18, color: "white" }}>{label}:</Typography>
    </Grid>
    <Grid item xs={4}>
      <Typography sx={{ fontSize: 18, color: "white", textAlign: "right" }}>{value}</Typography>
    </Grid>
  </Grid>
);

const ExcisePdfDialog = ({ open, source, onClose }) => {
  const [fileUrl, setFileUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !source?.fileId) {
      setFileUrl("");
      setError("");
      return undefined;
    }

    let objectUrl = "";
    const controller = new AbortController();

    const loadPdf = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await axios.get(`/api/v1/azureblobstorage/${source.fileId}`, {
          responseType: "blob",
          signal: controller.signal,
        });
        objectUrl = URL.createObjectURL(response.data);
        setFileUrl(objectUrl);
      } catch (requestError) {
        if (!controller.signal.aborted) {
          console.error("Unable to load excise PDF:", requestError);
          setError("The excise declaration PDF could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, source?.fileId]);

  const handleClose = () => {
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Excise declaration {source?.sourceCode || ""}
          </Typography>
          <IconButton aria-label="Close excise PDF preview" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 480 }}>
        {!source?.fileId && (
          <Alert severity="warning">This excise declaration does not have a PDF file.</Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && fileUrl && (
          <Box sx={{ height: "70vh" }}>
            <object data={fileUrl} type="application/pdf" width="100%" height="100%">
              <iframe title={`Excise PDF ${source?.sourceCode || ""}`} src={fileUrl} width="100%" height="100%" />
            </object>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" variant="contained" color="primary" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ExcisePreFinanceGroupDetailDialog = ({
  open,
  group,
  countryLabel,
  claimStates = [],
  option,
  isBusy,
  onClose,
  onGroupOptionChange,
}) => {
  const [pdfSource, setPdfSource] = useState(null);

  const claimStateById = useMemo(
    () =>
      claimStates.reduce((lookup, claimState) => {
        lookup[claimState.claimStateId] = claimState;
        return lookup;
      }, {}),
    [claimStates]
  );

  const getClaimStateLabel = (claimStateId) => {
    const claimState = claimStateById[claimStateId];
    return claimState?.description || claimState?.shortCode || claimStateId || "—";
  };

  if (!group) {
    return null;
  }

  const matchingClaims = group.matchingClaims || [];
  const selectedClaimId = option?.optionalClaimId ?? "";

  return (
    <>
      <Dialog
        open={open}
        onClose={isBusy ? undefined : onClose}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">View pre-finance group</Typography>
            <IconButton aria-label="Close prefinance group details" onClick={onClose} disabled={isBusy}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent
          dividers
          sx={{
            flex: "1 1 0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            {!group.isSelectable && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {group.exclusionReason || "This group cannot be selected."}
              </Alert>
            )}
            {group.missingSourceFileCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {group.missingSourceFileCount} excise declaration(s) do not have a PDF file.
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1}>
                  <StackField label="Country" value={countryLabel} />
                  <StackField label="Period" value={formatPreFinancePeriod(group)} />
                  <StackField label="Prefinance Code" value={group.previewPreFinanceCode} />
                  <StackField label="Version" value={group.nextVersion ?? "—"} />
                  <StackField
                    label="VF%"
                    value={`${formatPreFinanceMoney(group.appliedVfPercentage)}%`}
                  />
                  <StackField label="Liters" value={formatPreFinanceMoney(group.totalLiters)} />
                  <StackField
                    label="Amount EUR"
                    value={formatPreFinanceMoney(group.refundAmount)}
                  />
                  <StackField
                    label="Amount local"
                    value={formatPreFinanceMoney(group.declarationAmount)}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} md={6} sx={{ alignSelf: "flex-start" }}>
                <Box sx={{ p: 2, backgroundColor: "#376fd0" }}>
                  <SummaryPanelRow
                    label="Declaration Count"
                    value={group.sources.length}
                  />
                  <SummaryPanelRow label="Total Liters" value={formatPreFinanceMoney(group.totalLiters)} />
                  <SummaryPanelRow
                    label="Total Refund Amount"
                    value={formatPreFinanceMoney(group.refundAmount)}
                  />
                  <SummaryPanelRow
                    label="Total Payout Amount"
                    value={formatPreFinanceMoney(group.payoutAmount)}
                  />
                  <SummaryPanelRow label="Total Costs" value={formatPreFinanceMoney(group.totalCosts)} />
                  <SummaryPanelRow
                    label="Missing PDF Files"
                    value={group.missingSourceFileCount}
                  />
                </Box>
              </Grid>
            </Grid>

            <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 420 }}>
              <InputLabel id="excise-optional-claim-label">Linked claim reference</InputLabel>
              <Select
                labelId="excise-optional-claim-label"
                label="Linked claim reference"
                value={selectedClaimId}
                onChange={(event) =>
                  onGroupOptionChange(
                    group.groupKey,
                    "optionalClaimId",
                    event.target.value === "" ? null : Number(event.target.value)
                  )
                }
                disabled={isBusy || matchingClaims.length === 0}
              >
                <MenuItem value="">No claim reference</MenuItem>
                {matchingClaims.map((claim) => (
                  <MenuItem key={claim.claimId} value={claim.claimId}>
                    {claim.claimCode} · {getClaimStateLabel(claim.claimStateId)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {group.requiresExistingPeriodConfirmation && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" noWrap>
                  <strong>A dossier already exists for this exact period:</strong>{" "}
                  {group.exactExistingDossiers
                    .map(
                      (dossier) =>
                        `${dossier.preFinanceCode} · version ${dossier.version ?? "—"} · payout ${formatPreFinanceMoney(
                          dossier.payoutAmount
                        )}`
                    )
                    .join("; ")}
                </Typography>
                <FormControlLabel
                  sx={{ display: "flex", mt: 1 }}
                  control={
                    <Checkbox
                      checked={Boolean(option?.continueExistingPeriod)}
                      onChange={(event) =>
                        onGroupOptionChange(
                          group.groupKey,
                          "continueExistingPeriod",
                          event.target.checked
                        )
                      }
                    />
                  }
                  label="I confirm creating an additional pre-finance version for this period"
                />
              </Alert>
            )}
          </Box>

          <Box sx={{ flex: 1, minHeight: 200, height: 0, overflowY: "auto", pr: 0.5 }}>
            <Typography variant="subtitle1" gutterBottom>
              Linked excise declarations
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader aria-label="Linked excise declarations">
                <TableHead>
                  <TableRow>
                    <TableCell>Claim code</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Liters</TableCell>
                    <TableCell align="right">Amount EUR</TableCell>
                    <TableCell align="right">Amount local</TableCell>
                    <TableCell>PDF</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.sources.map((source) => (
                    <TableRow key={source.sourceId}>
                      <TableCell>{source.sourceCode}</TableCell>
                      <TableCell>{getClaimStateLabel(source.stateId)}</TableCell>
                      <TableCell align="right">{formatPreFinanceMoney(source.liters)}</TableCell>
                      <TableCell align="right">{formatPreFinanceMoney(source.refundAmount)}</TableCell>
                      <TableCell align="right">
                        {formatPreFinanceMoney(source.declarationAmount)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={source.fileId ? "Available" : "Missing"}
                          color={source.fileId ? "success" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={source.fileId ? "View PDF" : "No PDF available"}>
                          <span>
                            <IconButton
                              aria-label={`View PDF for ${source.sourceCode}`}
                              size="small"
                              disabled={!source.fileId}
                              onClick={() => setPdfSource(source)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", px: 3, py: 2, flexShrink: 0 }}>
          <Button type="button" variant="contained" color="primary" onClick={onClose} disabled={isBusy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ExcisePdfDialog
        open={Boolean(pdfSource)}
        source={pdfSource}
        onClose={() => setPdfSource(null)}
      />
    </>
  );
};

export default ExcisePreFinanceGroupDetailDialog;
